import React from 'react'
import styled from 'styled-components'

import { Button, type ButtonTheme } from '@nexiahome/ui-common/components/primitives/buttons'
import { ComponentStyleSheet } from '@nexiahome/ui-common/themes'

import useScreenSize from 'hooks/useScreenSize'
import { phoneNumber } from 'helpers/formatters'

import { color } from 'styles/colors'

type Props = {
  piiHomeContactFirstName: string | null
  piiHomeContactLastName: string | null
  piiHomeAddressLine1: string | null
  piiHomeAddressLine2: string | null
  piiHomeAddressCity: string | null
  piiHomeAddressState: string | null
  piiHomeAddressPostalCode: string | null
  piiHomeContactPhoneNumber: string | null
  piiHomeContactEmailAddress: string | null
  onClickCopy: () => void
}

const CustomersEntryPanel = ({
  piiHomeContactFirstName,
  piiHomeContactLastName,
  piiHomeAddressLine1,
  piiHomeAddressLine2,
  piiHomeAddressCity,
  piiHomeAddressState,
  piiHomeAddressPostalCode,
  piiHomeContactPhoneNumber,
  piiHomeContactEmailAddress,
  onClickCopy
}: Props) => {
  const screenSize = useScreenSize()
  const size = screenSize || 'large'
  return (
    <S.CustomersEntry screenSize={size}>
      <S.CustomersEntryPanel>
        <S.Title screenSize={size}>
          <h3>Customer's Entry</h3>
        </S.Title>

        <S.Grid screenSize={size}>
          <S.Label>First Name
            <S.Value>{piiHomeContactFirstName}</S.Value>
          </S.Label>
          <S.Label>Last Name
            <S.Value>{piiHomeContactLastName}</S.Value>
          </S.Label>
          <S.Label>Address 1
            <S.Value>{piiHomeAddressLine1}</S.Value>
          </S.Label>
          <S.Label>Address 2
            <S.Value>{piiHomeAddressLine2}</S.Value>
          </S.Label>
          <S.Label>City
            <S.Value>{piiHomeAddressCity}</S.Value>
          </S.Label>
          <S.CityZip>
            <div>State<S.Value>{piiHomeAddressState}</S.Value></div>
            <S.Zip screenSize={size}>Zip<S.Value>{piiHomeAddressPostalCode}</S.Value></S.Zip>
          </S.CityZip>
          <S.Label>Phone Number
            <S.Value>{phoneNumber(piiHomeContactPhoneNumber)}</S.Value>
          </S.Label>
          <S.Label>Email
            <S.Value>{piiHomeContactEmailAddress}</S.Value>
          </S.Label>
        </S.Grid>

        {screenSize === 'large' && <Button decoration="secondary" icon="arrow-left2" text="Copy" onPress={onClickCopy} styles={copyButtonStyle} />}
        {(screenSize === 'medium' || screenSize === 'small') && <Button decoration="secondary" icon="arrow-down2" text="Copy" onPress={onClickCopy} styles={copyButtonStyle} />}
      </S.CustomersEntryPanel>
    </S.CustomersEntry>
  )
}

export default CustomersEntryPanel

const copyButtonStyle: ComponentStyleSheet<ButtonTheme> = {
  button: {
    alignSelf: 'flex-start',
    marginLeft: 20
  }
}

const S = {
  CustomersEntry: styled.div.withConfig({
    shouldForwardProp: (prop) => !['screenSize'].includes(prop)
  })<{ screenSize: string }>`
    white-space: nowrap;
    width: calc(100% - 800px);
    min-width: 390px;
    max-width: 800px;
    margin: auto 30px;

    @media ${({ screenSize }) => screenSize} {
      width: 100%;
      margin: 0;
      padding: 15px;
      float: none;
    }

    @media ${({ screenSize }) => screenSize}{
      padding: 7px;
      min-width: auto;
    }
  `,
  CustomersEntryPanel: styled.div`
    display: flex;
    flex-direction: column;
    box-shadow: 0px 0px 1px;
    border-radius: 5px;
    border: 1px solid ${color('ancillaryInfoPanelBorder')};
    overflow: hidden;
    padding-bottom: 10px;
  `,
  Title: styled.div.withConfig({
    shouldForwardProp: (prop) => !['screenSize'].includes(prop)
  })<{ screenSize: string }>`
    padding: 10px 15px;
    background-color: ${color('ancillaryInfoHeaderBackground')};

    > h3 {
      margin: 0
    }

    @media ${({ screenSize }) => screenSize} {
      padding: 5px 10px;
    }
  `,
  Grid: styled.div.withConfig({
    shouldForwardProp: (prop) => !['screenSize'].includes(prop)
  })<{ screenSize: string }>`
    display: grid;
    grid-template-columns: 40% 60%;
    grid-auto-rows: 60px;
    padding: 15px 15px 0;

    @media ${({ screenSize }) => screenSize} {
      padding: 10px 10px 0;
      grid-auto-rows: 55px;
    }
  `,
  Label: styled.div`
    font-weight: lighter;
  `,
  Value: styled.div`
    font-weight: bold;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  `,
  CityZip: styled.div`
    display: -webkit-box;
  `,
  Zip: styled.div.withConfig({
    shouldForwardProp: (prop) => !['screenSize'].includes(prop)
  })<{ screenSize: string }>`
    padding-left: 70px;

    @media ${({ screenSize }) => screenSize} {
      padding-left: 30px;
    }
  `
}
